import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Button, styled } from '@mui/material';
import { SWR_ORDER_FILES_ENDPOINT } from 'hooks/swr/useOrderFiles';
import { isArray, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useState, type FC, useId, useCallback, type ChangeEvent } from 'react';
import { mutate } from 'swr';
import UploadProgress from '../../../core/components/UploadProgress';
import { useOrderPageStore } from '../store/useOrderPageStore';
import { uploadFiles } from './orderFilesUtils';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadButton: FC = observer(() => {
  const [fileUploads, setFileUploads] = useState<
    Array<{ file: File; progress: number; controller: AbortController }>
  >([]);

  const orderPageStore = useOrderPageStore();

  const componentId = useId();

  const handleFileInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const acceptedFiles = (
        isNil(event.target.files) ? [] : [...event.target.files]
      ).map((file) => ({
        file,
        progress: 0,
        controller: new AbortController(),
      }));

      setFileUploads((prevState) => [...prevState, ...acceptedFiles]);

      void uploadFiles(
        acceptedFiles,
        orderPageStore.order?.id ?? 0,
        (percentCompleted, i) => {
          setFileUploads((prevState) => {
            const updatedUploads = [...prevState];

            const item = updatedUploads[i];
            !isNil(item) && (item.progress = percentCompleted);
            return updatedUploads;
          });
        }
      ).then(async () => {
        setFileUploads([]);

        return await mutate(
          (key) => isArray(key) && key[0] === SWR_ORDER_FILES_ENDPOINT,
          undefined
        );
      });
    },
    [orderPageStore.order]
  );

  const handleCancelUpload = (file: File): void => {
    const uploadToCancel = fileUploads.find((upload) => upload.file === file);
    if (uploadToCancel) {
      uploadToCancel.controller.abort();
      setFileUploads((prevState) => prevState.filter((item) => item !== uploadToCancel));
    }
  };

  if (fileUploads.length > 0) {
    return (
      <>
        {fileUploads.map((upload, index) => (
          <Box key={`${componentId}-uploader-${index}`}>
            <UploadProgress
              progress={upload.progress}
              fileName={upload.file.name}
              onCancel={() => {
                handleCancelUpload(upload.file);
              }}
            />
          </Box>
        ))}
      </>
    );
  }

  return (
    <Button component='label' variant='outlined' startIcon={<FileUploadOutlinedIcon />}>
      Загрузить
      <VisuallyHiddenInput type='file' multiple onInput={handleFileInput} />
    </Button>
  );
});

export default UploadButton;
